import "@hotwired/turbo-rails";
import "./controllers";
import "fslightbox";
import Swiper from "swiper/bundle";
import SimpleParallax from "simple-parallax-js/vanilla";

const COOKIE_CONSENT_STORAGE_KEY = "keylink_cookie_consent_accepted";
const THEME_STORAGE_KEY = "theme";
let cookieConsentAccepted = document.documentElement.classList.contains(
  "cookie-consent-accepted",
);

function hasCookieConsent() {
  if (cookieConsentAccepted) return true;

  try {
    cookieConsentAccepted =
      window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === "true";
  } catch (_error) {
    cookieConsentAccepted = false;
  }

  return cookieConsentAccepted;
}

function setCookieConsent(value) {
  cookieConsentAccepted = value === true;
  document.documentElement.classList.toggle(
    "cookie-consent-accepted",
    cookieConsentAccepted,
  );

  try {
    if (cookieConsentAccepted) {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, "true");
    } else {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    }
  } catch (_error) {
    // Ignore storage write errors (e.g., private mode restrictions)
  }
}

function getCurrentTheme() {
  return document.documentElement.classList.contains("red-theme")
    ? "red"
    : "light";
}

function getStoredTheme() {
  try {
    const theme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return theme === "red" || theme === "light" ? theme : null;
  } catch (_error) {
    return null;
  }
}

function setTheme(theme, { persist = false } = {}) {
  const root = document.documentElement;
  root.classList.remove("light-theme", "red-theme");
  root.classList.add(`${theme}-theme`);

  if (!persist) return;

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (_error) {
    // Ignore storage write errors (e.g., private mode restrictions)
  }
}

function syncThemeSwitcherUi({ thumb, switcher, theme }) {
  if (!thumb) return;

  if (theme === "light") {
    thumb.classList.add("translate-x-[33px]");
    thumb.style.backgroundColor = "var(--color-light)";
    switcher?.setAttribute("aria-checked", "true");
    return;
  }

  thumb.classList.remove("translate-x-[33px]");
  thumb.style.backgroundColor = "var(--color-red-theme)";
  switcher?.setAttribute("aria-checked", "false");
}

function setThemeSwitcherAvailability(switcher, enabled) {
  if (!switcher) return;

  switcher.setAttribute("aria-disabled", enabled ? "false" : "true");
  switcher.setAttribute("tabindex", enabled ? "0" : "-1");
  switcher.classList.toggle("pointer-events-none", !enabled);
  switcher.classList.toggle("opacity-50", !enabled);
}

function setCookieBannerVisibility(banner, consentAccepted) {
  if (!banner) return;

  banner.classList.toggle("hidden", consentAccepted);
  banner.setAttribute("aria-hidden", consentAccepted ? "true" : "false");
}

function initSwipers() {
  document.querySelectorAll(".swiper").forEach((el) => {
    if (el.swiper) return;
    try {
      const swiper = new Swiper(el, {
        loop: true,
        navigation: {
          nextEl: el.querySelector(".swiper-button-next"),
          prevEl: el.querySelector(".swiper-button-prev"),
        },
        pagination: {
          el: el.querySelector(".swiper-pagination"),
          type: "fraction",
        },
        autoplay: {
          delay: 10000,
          disableOnInteraction: false,
        },
        autoHeight: true,
      });
      const clickPrev = el.querySelector(".swiper-click-prev");
      const clickNext = el.querySelector(".swiper-click-next");
      clickPrev?.addEventListener("click", () => swiper.slidePrev());
      clickNext?.addEventListener("click", () => swiper.slideNext());
    } catch (e) {
      console.error("Ошибка при инициализации Swiper:", e);
    }
  });
}

function initFilePreview() {
  const tryInit = () => {
    const input = document.querySelector(
      '#new-gallery-slide-form input[type="file"]',
    );
    const previewContainer = document.getElementById("gallery-preview");
    if (!input || !previewContainer) return setTimeout(tryInit, 100);
    input.addEventListener("change", () => {
      previewContainer.innerHTML = "";
      Array.from(input.files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = document.createElement("img");
          img.src = e.target.result;
          img.className = "w-24 h-24 object-cover rounded shadow";
          previewContainer.appendChild(img);
        };
        reader.readAsDataURL(file);
      });
    });
  };
  tryInit();
}

function initFsLightbox() {
  document.querySelectorAll(".fullscreen-btn").forEach((btn) => {
    const galleryName = btn.dataset.gallery;
    btn.addEventListener("click", () => {
      const instance = window.fsLightboxInstances?.[galleryName];
      if (instance) {
        window.refreshFsLightbox?.();
        instance.open();
      }
    });
  });
  setTimeout(() => window.refreshFsLightbox?.(), 0);
}

function initScrollTo() {
  document.querySelectorAll("[data-scroll-to]").forEach((anchor) => {
    anchor.addEventListener("click", (e) => {
      e.preventDefault();
      const targetId = anchor.dataset.scrollTo;
      document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
    });
  });
}

function goBack() {
  if (window.history.length > 1) window.history.back();
  else window.location.href = "/";
}
window.goBack = goBack;

function initCookieConsentBanner() {
  const banner = document.getElementById("cookie-banner");
  if (!banner) return;

  setCookieBannerVisibility(banner, hasCookieConsent());
  if (hasCookieConsent()) return;

  const acceptButton = document.getElementById("cookie-banner-accept");
  if (!acceptButton || acceptButton.dataset.initialized === "true") return;

  acceptButton.dataset.initialized = "true";
  acceptButton.addEventListener("click", () => {
    setCookieConsent(true);
    setCookieBannerVisibility(banner, true);
    initThemeSwitcher();
  });
}

function initThemeSwitcher() {
  const switcher = document.getElementById("theme-switcher");
  const thumb = document.getElementById("switch-thumb");
  if (!switcher || !thumb) return;

  const applyTheme = (theme) => {
    setTheme(theme);
    syncThemeSwitcherUi({ thumb, switcher, theme });
  };

  const syncState = () => {
    const consentAccepted = hasCookieConsent();
    const savedTheme = consentAccepted ? getStoredTheme() : null;
    setThemeSwitcherAvailability(switcher, consentAccepted);
    applyTheme(savedTheme || (consentAccepted ? getCurrentTheme() : "light"));
  };

  syncState();
  if (switcher.dataset.initialized === "true") return;

  switcher.dataset.initialized = "true";
  switcher.addEventListener("click", () => {
    if (!hasCookieConsent()) return;
    const newTheme = getCurrentTheme() === "light" ? "red" : "light";
    setTheme(newTheme, { persist: true });
    syncThemeSwitcherUi({ thumb, switcher, theme: newTheme });
  });
  switcher.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      if (!hasCookieConsent()) return;
      const newTheme = getCurrentTheme() === "light" ? "red" : "light";
      setTheme(newTheme, { persist: true });
      syncThemeSwitcherUi({ thumb, switcher, theme: newTheme });
    }
  });
}

function parallaxInit() {
  var images = document.querySelectorAll(".parallaxed img");
  new SimpleParallax(images, {
    scale: 1.1,
    delay: 0.5,
  });
}

function initRoundedCorners() {
  const mainTag = document.querySelector("main");
  if (window.location.pathname === "/contacts") {
    mainTag.classList.remove("bg-footer");
  } else {
    mainTag.classList.add("bg-footer");
  }
}

document.addEventListener("DOMContentLoaded", parallaxInit);

document.addEventListener("turbo:before-render", (event) => {
  const newBody = event.detail.newBody;
  const banner = newBody.querySelector("#cookie-banner");
  const switcher = newBody.querySelector("#theme-switcher");
  const thumb = newBody.querySelector("#switch-thumb");
  const consentAccepted = hasCookieConsent();
  const theme = consentAccepted ? getCurrentTheme() : "light";

  syncThemeSwitcherUi({ thumb, switcher, theme });
  setThemeSwitcherAvailability(switcher, consentAccepted);
  setCookieBannerVisibility(banner, consentAccepted);
});

document.addEventListener("turbo:load", () => {
  parallaxInit();
  initSwipers();
  initFilePreview();
  initFsLightbox();
  initScrollTo();
  initCookieConsentBanner();
  initThemeSwitcher();
  initRoundedCorners();

  const platform = navigator.userAgentData?.platform || navigator.platform;

  const isWindows = /Win/i.test(platform);

  if (isWindows) {
    document.documentElement.classList.add("windows-platform");
  }
});
